import { Fragment, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { useEntitlement } from '../lib/useEntitlement';
import { ButtonLink } from './ui/Button';
import { Home, Map, Calendar, Heart, BookOpen } from 'lucide-react';
// Vite will content-hash these on build → URL changes whenever
// the SVG bytes change → no stale browser caches even on Safari.
import lockupSvg from '../brand/hattie-lockup.svg';

/**
 * Plan §20.6 — site shell.
 *
 * Desktop: top header with the Hattie lockup + primary nav (Today,
 * Map, Saved, Journal, Settings). Sign-in / avatar on the right.
 * Footer with Privacy / Terms / Press links + the locked tagline.
 *
 * Mobile (May 22 2026, map-rework spec): the 6-tab top nav strip
 * moves to a 5-item bottom tab bar (Today / Map / Planner / Saved
 * / Journal), thumb-reachable. Settings folds into the avatar
 * dropdown (already works the same way on desktop). The top header
 * shrinks to just lockup + avatar — gets out of the way so the
 * map / today / journal pages get more vertical breathing room.
 * Marketing pages keep no nav (showAppNav=false).
 */
export function Shell({ children }: { children: ReactNode }) {
    const { pathname } = useLocation();
    const isMarketing =
        pathname === '/' ||
        pathname === '/data' ||
        pathname === '/press' ||
        pathname === '/privacy' ||
        pathname === '/terms';

    // The map page is a viewport-locked single-screen experience
    // (no scroll, no footer). Every other page scrolls naturally.
    // On the map page we use h-[100dvh] so the page is exactly the
    // visible viewport on iOS Safari (which excludes the URL bar)
    // and overflow-hidden so nothing inside can spill out.
    const isMapPage = pathname === '/map';

    if (isMapPage) {
        // Map page: viewport-locked flex column. Bottom nav is a
        // flex item (relative) so it OCCUPIES space — main shrinks
        // and the map renders ABOVE the nav, not behind it. Pins
        // never hide under the tab bar.
        return (
            <div className="h-[100dvh] flex flex-col bg-hattie-cream overflow-hidden">
                <div className="shrink-0">
                    <Header showAppNav={true} />
                </div>
                <main className="flex-1 flex flex-col min-h-0">{children}</main>
                <MobileBottomNav position="relative" />
            </div>
        );
    }

    return (
        <div className="min-h-full flex flex-col bg-hattie-cream">
            <Header showAppNav={!isMarketing} />
            {/* Scrolling pages: bottom nav is fixed (stays visible
              while content scrolls). Pad the main bottom so the
              last bit of content + the footer don't sit BEHIND
              the fixed bar. md+ gets no padding since the bottom
              nav is hidden there. */}
            <main className={`flex-1 flex flex-col ${!isMarketing ? 'pb-[calc(env(safe-area-inset-bottom,0px)+3.5rem)] md:pb-0' : ''}`}>
                {children}
            </main>
            {!isMarketing && <MobileBottomNav position="fixed" />}
            <Footer />
        </div>
    );
}

function Header({ showAppNav }: { showAppNav: boolean }) {
    const { isSignedIn, user, signOut } = useAuth();
    // Plan §37 S12 — Clubs entry-point is SERVER-DRIVEN (/v1/me
    // clubs.enabled = flag ∨ member ∨ pilot). Desktop nav gets the
    // item; mobile keeps its 5 tabs and reaches Clubs via the
    // avatar menu + Today card.
    const { clubsEnabled } = useEntitlement();
    const nav = clubsEnabled
        ? [...APP_NAV.slice(0, -1), { to: '/clubs', label: 'Clubs' }, APP_NAV[APP_NAV.length - 1]!]
        : APP_NAV;
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    void navigate;

    return (
        <header className="border-b border-hattie-stone bg-hattie-cream/85 backdrop-blur sticky top-0 z-30">
            <div className="max-w-6xl mx-auto px-3 md:px-4 py-2 md:py-3 flex items-center gap-3 md:gap-6">
                <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0" aria-label="Hattie Knows Wild Swim">
                    <img src={lockupSvg} alt="Hattie Knows Wild Swim" className="h-10 md:h-20 w-auto" />
                </Link>

                {showAppNav && (
                    <nav className="hidden md:flex items-center gap-1 text-sm font-medium text-hattie-mist">
                        {nav.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) =>
                                    `px-3 py-1.5 rounded-full transition-colors ${
                                        isActive
                                            ? 'bg-hattie-teal text-white'
                                            : 'hover:bg-hattie-paper hover:text-hattie-ink'
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                )}

                <div className="flex-1" />

                {!showAppNav && (
                    <Link to="/today" className="hidden sm:inline-flex">
                        <span className="rounded-full bg-hattie-teal text-white text-sm font-semibold py-1.5 px-4 hover:bg-hattie-teal/90">
                            Open the app
                        </span>
                    </Link>
                )}

                {showAppNav && (
                    <Fragment>
                        {isSignedIn ? (
                            <div className="relative">
                                <button
                                    onClick={() => setMenuOpen((m) => !m)}
                                    className="flex items-center gap-2 rounded-full border border-hattie-stone hover:bg-hattie-paper px-2 py-1 transition-colors"
                                    aria-label="Account menu"
                                >
                                    {user?.photoURL ? (
                                        <img src={user.photoURL} alt="" className="w-7 h-7 rounded-full" />
                                    ) : (
                                        <span className="w-7 h-7 rounded-full bg-hattie-teal text-white text-xs flex items-center justify-center font-semibold">
                                            {(user?.displayName ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
                                        </span>
                                    )}
                                </button>
                                {menuOpen && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl border border-hattie-stone shadow-lg p-1 z-40">
                                        <div className="px-3 py-2 border-b border-hattie-stone/60">
                                            <div className="text-sm font-semibold text-hattie-ink truncate">{user?.displayName ?? 'Signed in'}</div>
                                            {user?.email && <div className="text-xs text-hattie-mist truncate">{user.email}</div>}
                                        </div>
                                        {clubsEnabled && (
                                            <Link
                                                to="/clubs"
                                                onClick={() => setMenuOpen(false)}
                                                className="block px-3 py-2 text-sm text-hattie-ink hover:bg-hattie-paper rounded-xl"
                                            >
                                                Clubs
                                            </Link>
                                        )}
                                        <Link
                                            to="/settings"
                                            onClick={() => setMenuOpen(false)}
                                            className="block px-3 py-2 text-sm text-hattie-ink hover:bg-hattie-paper rounded-xl"
                                        >
                                            Settings
                                        </Link>
                                        <button
                                            onClick={async () => {
                                                setMenuOpen(false);
                                                await signOut();
                                            }}
                                            className="block w-full text-left px-3 py-2 text-sm text-hattie-ink hover:bg-hattie-paper rounded-xl"
                                        >
                                            Sign out
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <ButtonLink variant="outline" size="sm" to="/settings">
                                Sign in
                            </ButtonLink>
                        )}
                    </Fragment>
                )}
            </div>

            {/* The top mobile nav strip used to sit here. Moved to
              a bottom tab bar (MobileBottomNav) — thumb-reachable +
              gives the map / today / journal pages their full
              vertical height back. */}
        </header>
    );
}

/**
 * MobileBottomNav — fixed bottom tab bar, mobile only.
 *
 * Five primary destinations: Today / Map / Planner / Saved /
 * Journal. Settings is reached via the avatar dropdown in the
 * top header (consistent with desktop). Hidden at md+ where the
 * full top header nav still shows.
 *
 * Sized for thumb-reachability: 56px row height + a safe-area
 * inset for iPhones with home-indicators. Icon + label on each
 * tab (label = 11px so a 360px viewport fits all five without
 * truncation).
 *
 * Active state matches the top-header nav: brand-teal pill on the
 * icon + ink label. Inactive: muted icon + label.
 */
function MobileBottomNav({ position }: { position: 'fixed' | 'relative' }) {
    const positionClass =
        position === 'fixed'
            ? 'fixed bottom-0 left-0 right-0 z-30'
            : 'relative shrink-0 z-30';
    return (
        <nav
            className={`md:hidden ${positionClass} bg-hattie-cream/95 backdrop-blur border-t border-hattie-stone pointer-events-auto`}
            aria-label="Primary navigation"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
            <ul className="flex items-stretch justify-around">
                {BOTTOM_NAV.map((item) => (
                    <li key={item.to} className="flex-1">
                        <NavLink
                            to={item.to}
                            className={({ isActive }) =>
                                `flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium transition-colors ${
                                    isActive
                                        ? 'text-hattie-teal'
                                        : 'text-hattie-mist hover:text-hattie-ink'
                                }`
                            }
                            aria-label={item.label}
                        >
                            {({ isActive }) => (
                                <>
                                    <item.icon className={`w-5 h-5 ${isActive ? 'text-hattie-teal' : ''}`} />
                                    <span>{item.label}</span>
                                </>
                            )}
                        </NavLink>
                    </li>
                ))}
            </ul>
        </nav>
    );
}

function Footer() {
    return (
        <footer className="border-t border-hattie-stone bg-hattie-paper mt-12">
            <div className="max-w-6xl mx-auto px-4 py-8 grid gap-6 md:grid-cols-3">
                <div>
                    <img src={lockupSvg} alt="Hattie Knows Wild Swim" className="h-16 w-auto mb-2" />
                    <p className="text-sm text-hattie-mist max-w-xs">
                        The most honest verdict on UK water. Built in the UK, independent, ad-free.
                    </p>
                </div>
                <div className="text-sm">
                    <h3 className="font-semibold text-hattie-ink mb-2">App</h3>
                    <ul className="space-y-1 text-hattie-mist">
                        <li><Link to="/today" className="hover:text-hattie-ink">Today</Link></li>
                        <li><Link to="/map" className="hover:text-hattie-ink">Map</Link></li>
                        <li><Link to="/swimming" className="hover:text-hattie-ink">Swimming by region</Link></li>
                        <li><Link to="/data" className="hover:text-hattie-ink">How we built our data</Link></li>
                        <li><Link to="/water-companies" className="hover:text-hattie-ink">Water companies</Link></li>
                    </ul>
                </div>
                <div className="text-sm">
                    <h3 className="font-semibold text-hattie-ink mb-2">Legal &amp; press</h3>
                    <ul className="space-y-1 text-hattie-mist">
                        <li><Link to="/support" className="hover:text-hattie-ink">Support</Link></li>
                        <li><Link to="/privacy" className="hover:text-hattie-ink">Privacy</Link></li>
                        <li><Link to="/terms" className="hover:text-hattie-ink">Terms</Link></li>
                        <li><Link to="/press" className="hover:text-hattie-ink">Press kit</Link></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-hattie-stone/60 text-xs text-hattie-mist text-center py-3 px-4">
                © {new Date().getFullYear()} Hattie Knows. Open data from EA, NRW, SEPA, DAERA, Met Office, all 12 UK water companies. We do not contact emergency services on your behalf — call 999.
            </div>
        </footer>
    );
}

const APP_NAV = [
    { to: '/today', label: 'Today' },
    { to: '/map', label: 'Map' },
    { to: '/planner', label: 'Planner' },
    { to: '/saved', label: 'Saved' },
    { to: '/journal', label: 'Journal' },
    { to: '/settings', label: 'Settings' },
];

// Mobile bottom nav — 5 items. Settings folds into the avatar
// dropdown (consistent with desktop), so it's NOT in this list.
const BOTTOM_NAV = [
    { to: '/today', label: 'Today', icon: Home },
    { to: '/map', label: 'Map', icon: Map },
    { to: '/planner', label: 'Planner', icon: Calendar },
    { to: '/saved', label: 'Saved', icon: Heart },
    { to: '/journal', label: 'Journal', icon: BookOpen },
];
